// imports
import { useEffect, useRef, useState } from 'react'
import { usePopper } from 'react-popper'
import type { Placement } from '@popperjs/core'

// utils
import useElementState from '@/hooks/useElementState'
import useWindowSize from '@/hooks/useWindowSize'
import { arrowOffsetModifier, getArrowClass, getSide } from './arrow'
import { fitHeightModifier } from './fitHeight'
import type { PopoverProps } from './types/PopoverTypes'

// styles
import './Popover.scss'

// space between the target and the popover, the arrow (11px) sits in it
const TARGET_DISTANCE = 16
// by default the popover keeps this distance from the edges of the screen
const SCREEN_DISTANCE = 20

/**
 * A popover next to `sourceElement`. Popper places it on the side given by `position` (`positionMobile` on mobile),
 * centred on the target. It switches to the opposite side when there is not enough room, it is moved to stay inside
 * the screen, and its arrow always points at the centre of the target. With `fitHeight` it stays on its side instead
 * and is not higher than the room it has there (as `max-height`, so its content has to scroll). Clicking outside (a
 * click on the target itself is not outside, so that the target can toggle the popover) or Escape calls `onClose`.
 */
const Popover = ({
    sourceElement,
    content,
    onClose,
    position = 'bottom',
    positionMobile = 'left',
    customClass = '',
    screenDistance = SCREEN_DISTANCE,
    fitHeight = false
}: PopoverProps) => {
    const { ref: popoverRef, isClickedOutside: isClickedOutsideFlyout } =
        useElementState(sourceElement)
    const [popoverElement, setPopoverElement] = useState<HTMLDivElement | null>(null)
    const { isMobile } = useWindowSize()

    const placement: Placement = getSide(isMobile && positionMobile ? positionMobile : position)

    const { styles, attributes, update } = usePopper(sourceElement, popoverElement, {
        placement,
        modifiers: [
            { name: 'offset', options: { offset: [0, TARGET_DISTANCE] } },
            // a popover that fits its height into the room it has stays on its side of the target
            { name: 'flip', enabled: !fitHeight, options: { padding: screenDistance } },
            { name: 'preventOverflow', options: { padding: screenDistance } },
            { ...fitHeightModifier, enabled: fitHeight, options: { padding: screenDistance } },
            // top / left instead of a transform, so that the pages can still move the popover with their own styles
            { name: 'computeStyles', options: { gpuAcceleration: false, adaptive: false } },
            arrowOffsetModifier,
        ],
    })

    // Read through a ref: a new `onClose` of the parent must not call it again while the click outside is still set
    const onCloseRef = useRef(onClose)
    onCloseRef.current = onClose

    useEffect(() => {
        if (isClickedOutsideFlyout) {
            onCloseRef.current()
        }
    }, [isClickedOutsideFlyout])

    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onClose()
        }
        document.addEventListener('keydown', onKeyDown)

        return () => document.removeEventListener('keydown', onKeyDown)
    }, [onClose])

    // Popper only measures when it is told to: the content can change its size after the popover is shown (e.g. a
    // selection is removed) and the target can move without the popover changing at all (e.g. the input area
    // grows or shrinks), so both are checked every frame
    const updateRef = useRef(update)
    updateRef.current = update

    useEffect(() => {
        if (!sourceElement || !popoverElement) return

        const measure = () => {
            const target = sourceElement.getBoundingClientRect()

            return [target.left, target.top, target.width, target.height, popoverElement.offsetWidth, popoverElement.offsetHeight].join()
        }

        let measured = measure()
        let frame = requestAnimationFrame(function check() {
            const current = measure()
            // Popper is not ready in the first moments: a change is only taken over once it was passed on
            if (current !== measured && updateRef.current) {
                measured = current
                void updateRef.current()
            }
            frame = requestAnimationFrame(check)
        })

        return () => cancelAnimationFrame(frame)
    }, [sourceElement, popoverElement])

    const setRefs = (element: HTMLDivElement | null) => {
        popoverRef.current = element
        setPopoverElement(element)
    }

    const actualPlacement = attributes.popper?.['data-popper-placement'] as Placement | undefined

    return (
        <div
            ref={setRefs}
            className={`flyout-popover ${getArrowClass(actualPlacement ?? placement)} m-popover -detached ${customClass}`}
            style={styles.popper}
            {...attributes.popper}
        >
            <div className="a-box -floating">
                <div className="m-popover__content">
                    <div className="m-popover__paragraph">{content}</div>
                </div>
            </div>
        </div>
    )
}

export default Popover
